/**
 * Mappers Index - Centralized exports for all entity mappers
 */

export * from './BaseMapper';
export * from './ProjectMapper';
export * from './UserMapper';
export * from './AssetMapper';
export * from './AssetEntityConfig';

// Re-export mapper instances for easy access
export { projectMapper } from './ProjectMapper';
export { userMapper } from './UserMapper';
export { 
  assetGeneralInfoMapper, 
  assetHierarchyLevelMapper, 
  assetDetailsMapper, 
  assetAttributesSchemaMapper,
  assetTypeMapper,
  assetAttributesMapper 
} from './AssetMapper';

// Re-export types from generated API for backward compatibility
export type { components } from '@/core/types/generated/api';

// Import types and instances for compatibility functions
import type { ProjectDto } from './ProjectMapper';
import type { UserDto } from './UserMapper';
import { projectMapper } from './ProjectMapper';
import { userMapper } from './UserMapper';

// Backward compatibility type aliases (matching api-mappers.ts interfaces)
export interface UiProjectMember { 
  id?: string; 
  userId?: string; 
  fullName?: string | null; 
  email?: string | null;
  projectId?: string; 
  role?: string | null; 
}

export interface UiProject {
  id?: string; 
  name?: string | null; 
  description?: string | null; 
  isActive?: boolean; 
  status?: import('@/core/types/models/Project').ProjectStatusType; 
  statusLabel?: string;
  icon?: string; 
  createdAt?: Date; 
  lastModifiedAt?: Date | null; 
  createdBy?: string | null;
  lastModifiedBy?: string | null;
  members: UiProjectMember[];
}

export interface UiUser { 
  id?: string;
  username?: string;
  email: string; 
  fullName: string; 
  designation?: string | null;
  isActive?: boolean; 
  role?: import('@/core/types/models/RoleTypes').SystemRoleType | number | null;
  lastLogin?: Date | null;
  createdAt?: Date;
  createdBy?: string | null;
  lastModifiedAt?: Date | null;
  lastModifiedBy?: string | null;
  projectMemberships?: any[] | null;
}

// Standalone function exports for backward compatibility with api-mappers.ts
export const mapProject = (dto: ProjectDto): UiProject => {
  const mapped = projectMapper.mapToUiModel(dto);
  return {
    id: mapped.id || undefined,
    name: mapped.name || undefined,
    description: mapped.description || undefined,
    isActive: mapped.status !== 'Archived', // Derive isActive from status
    status: mapped.status,
    icon: mapped.icon,
    createdAt: mapped.createdAt,
    lastModifiedAt: mapped.lastModifiedAt,
    createdBy: mapped.createdBy,
    lastModifiedBy: mapped.lastModifiedBy,
    members: mapped.members.map(m => ({
      userId: m.userId || undefined,
      role: m.role || null,
      fullName: m.fullName || null,
      email: m.email ?? null,
      id: undefined,
      projectId: mapped.id
    }))
  };
};

export const mapUser = (dto: UserDto): UiUser => {
  const mapped = userMapper.mapToUiModel(dto);
  return {
    id: mapped.id,
    username: mapped.username,
    email: mapped.email,
    fullName: mapped.fullName,
    designation: mapped.designation,
    isActive: mapped.isActive,
    role: mapped.role,
    lastLogin: mapped.lastLogin,
    createdAt: mapped.createdAt,
    createdBy: mapped.createdBy,
    lastModifiedAt: mapped.lastModifiedAt,
    lastModifiedBy: mapped.lastModifiedBy,
    projectMemberships: mapped.projectMemberships,
  };
};

// New helpers for mapping labels to API string enums
export const mapProjectStatusToApi = (statusOrLabel: string | undefined) => projectMapper.mapStatusToApi(statusOrLabel);
export const mapProjectRoleToApi = (roleOrLabel: string | undefined) => projectMapper.mapRoleToApi(roleOrLabel);
